import { useState, useRef } from 'react';
import { useMenu } from '../context/MenuContext';
import { ItemCard } from './ItemCard';
import { ComplementPicker } from './ComplementPicker';
import { useI18n } from '../i18n';
import type { MenuItem, MenuGroup } from '../types';

export function MenuView() {
  const { groups, getGroupItems, getItemComplements, widgetHeroImage } = useMenu();
  const { t } = useI18n();

  const baseGroups = groups.filter((g) => g.base && g.visible_online);
  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const [pickerItem, setPickerItem] = useState<{
    item: MenuItem;
    complements: MenuGroup[];
  } | null>(null);

  function handleSelectItem(item: MenuItem) {
    const complements = getItemComplements(item.id);
    setPickerItem({ item, complements });
  }

  function scrollToGroup(groupId: number) {
    sectionRefs.current[groupId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="hc-menu">
      {/* Hero banner */}
      {widgetHeroImage && (
        <div className="hc-menu-hero">
          <img src={widgetHeroImage} alt="" />
        </div>
      )}

      {/* Sticky nav bar for quick jump */}
      {baseGroups.length > 1 && (
        <nav className="hc-category-nav">
          {baseGroups.map((group) => (
            <button
              key={group.id}
              className="hc-category-nav-btn"
              onClick={() => scrollToGroup(group.id)}
            >
              {group.name}
            </button>
          ))}
        </nav>
      )}

      {/* All groups rendered vertically */}
      {baseGroups.map((group) => {
        const items = getGroupItems(group.id);
        if (items.length === 0) return null;

        return (
          <div
            key={group.id}
            className="hc-menu-section"
            ref={(el) => { sectionRefs.current[group.id] = el; }}
          >
            <div className="hc-menu-section-header">
              <h2>{group.name}</h2>
              {group.description && <p>{group.description}</p>}
            </div>
            <div className="hc-item-grid">
              {group.img_url && (
                <div className="hc-group-hero">
                  <img src={group.img_url} alt={group.name} />
                </div>
              )}
              {items.map((item) => (
                <ItemCard key={item.id} item={item} onSelect={handleSelectItem} />
              ))}
            </div>
          </div>
        );
      })}

      {pickerItem && (
        <ComplementPicker
          item={pickerItem.item}
          complementGroups={pickerItem.complements}
          onClose={() => setPickerItem(null)}
        />
      )}
    </div>
  );
}
